import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Stack, Typography } from '@mui/material';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import type { BoardSection, Entry, Problem, RunMode, RunOptions, RunSpeed } from '../../../services/timetableService';
import ProblemList from '../ProblemList';
import OptionCard from './OptionCard';
import { entrySectionKey } from './boardModel';

interface GenerateDialogProps {
  open: boolean;
  onClose: () => void;
  onStart: (options: RunOptions) => Promise<boolean>;
  entries: Entry[];
  sections: BoardSection[];
  /** The class on screen, offered as the starting choice. */
  currentSection: string | null;
  problems: Problem[];
  sessionId: string;
}

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{children}</Typography>
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Generate the draft, or just some classes of it: fill around what is placed
 * or plan again keeping locked periods. A run for some classes leaves the
 * others as they are, or may shift them a little after you confirm.
 */
const GenerateDialog: React.FC<GenerateDialogProps> = ({ open, onClose, onStart, entries, sections, currentSection, problems, sessionId }) => {
  const [whole, setWhole] = useState(true);
  const [chosen, setChosen] = useState<string[]>([]);
  const [allowMoves, setAllowMoves] = useState(false);
  const [mode, setMode] = useState<RunMode>('fill');
  const [speed, setSpeed] = useState<RunSpeed>('quick');
  const [busy, setBusy] = useState(false);

  const scope = useMemo(() => (whole ? [] : chosen), [whole, chosen]);
  const inScope = useMemo(() => {
    const set = new Set(scope);
    return (key: string) => whole || set.has(key);
  }, [whole, scope]);
  const scoped = entries.filter((e) => inScope(entrySectionKey(e)));
  const placed = scoped.reduce((n, e) => n + e.span, 0);
  const locked = scoped.filter((e) => e.locked).reduce((n, e) => n + e.span, 0);
  // Only problems in the chosen classes (or the whole school's) stop this run.
  const errors = problems.filter((p) => p.severity === 'error' && (!scope.length || !p.sections || p.sections.some(inScope)));
  const options = useMemo(() => sections.map((s) => ({ value: s.key, label: s.label })), [sections]);

  useEffect(() => {
    if (!open) return;
    setWhole(true);
    setChosen(currentSection ? [currentSection] : []);
    setAllowMoves(false);
    setSpeed('quick');
  }, [open, currentSection]);

  useEffect(() => { if (open) setMode(placed ? 'fill' : 'fresh'); }, [open, placed]);

  const start = async () => {
    setBusy(true);
    const ok = await onStart({ mode, speed, scope, allowMoves: !whole && allowMoves });
    setBusy(false);
    if (ok) onClose();
  };

  const where = whole ? 'the school' : chosen.length === 1 ? options.find((o) => o.value === chosen[0])?.label ?? 'this class' : 'these classes';

  return (
    <ConfirmationDialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      onConfirm={start}
      title="Generate timetable"
      message={whole
        ? 'Every class, teacher and room is planned together, so nothing clashes.'
        : 'Only the chosen classes are planned, around everything already in the timetable.'}
      confirmText="Generate"
      confirmColor="primary"
      loading={busy}
      confirmDisabled={errors.length > 0 || (!whole && !chosen.length)}
    >
      <Stack spacing={2} sx={{ mt: 1.5 }}>
        <Stack spacing={1}>
          <Heading>Which classes</Heading>
          <Stack direction="row" spacing={1}>
            <OptionCard selected={whole} onSelect={() => setWhole(true)} title="Whole school" detail="Plan every class" />
            <OptionCard selected={!whole} onSelect={() => setWhole(false)} title="Chosen classes" detail="Leave the rest as they are" />
          </Stack>
          <Collapse in={!whole} unmountOnExit>
            <Stack spacing={1} sx={{ pt: 0.5 }}>
              <SearchableMultiSelect
                values={chosen}
                onChange={setChosen}
                options={options}
                placeholder="Pick classes"
                size="small"
                fullWidth
              />
              <OptionCard
                selected={!allowMoves}
                onSelect={() => setAllowMoves(false)}
                title="Keep other classes as they are"
                detail="Anything that doesn't fit is left to place by hand."
              />
              <OptionCard
                selected={allowMoves}
                onSelect={() => setAllowMoves(true)}
                title="Let other classes shift a little"
                detail="Only if it helps everything fit. You'll see each change before it's applied."
              />
            </Stack>
          </Collapse>
        </Stack>

        {errors.length ? (
          <Stack spacing={1}>
            <Heading>Fix these first</Heading>
            <ProblemList problems={errors} sessionId={sessionId} />
          </Stack>
        ) : (
          <>
            {placed > 0 && (whole || chosen.length > 0) && (
              <Stack spacing={1}>
                <Heading>What to keep in {where}</Heading>
                <OptionCard
                  selected={mode === 'fill'}
                  onSelect={() => setMode('fill')}
                  title="Fill the gaps"
                  detail={`Keep the ${plural(placed, 'placed period')} and place the rest around them.`}
                />
                <OptionCard
                  selected={mode === 'fresh'}
                  onSelect={() => setMode('fresh')}
                  title="Plan again"
                  detail={locked
                    ? `Keep the ${plural(locked, 'locked period')}; everything else may move.`
                    : 'Nothing is locked, so every period may move.'}
                />
              </Stack>
            )}
            <Stack spacing={1}>
              <Heading>Effort</Heading>
              <Stack direction="row" spacing={1}>
                <OptionCard selected={speed === 'quick'} onSelect={() => setSpeed('quick')} title="Quick" detail="Up to a minute" />
                <OptionCard selected={speed === 'thorough'} onSelect={() => setSpeed('thorough')} title="Thorough" detail="Up to 3 minutes, fewer gaps" />
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </ConfirmationDialog>
  );
};

export default GenerateDialog;
